import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Dialog, DialogContent, Grid } from '@mui/material';
import { ConfigurationStatus } from '@prisma/client';
import { Formik } from 'formik';

import { MetaTags, useQuery } from '@redwoodjs/web';

import { CustomInput } from 'src/components/CustomInput';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { REMOVE_STOCK_REASONS_TABLE_HEADERS } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import {
    CREATE_REMOVE_STOCK_REASON_MUTATION,
    DELETE_REMOVE_STOCK_REASON_MUTATION,
} from 'src/graphql/mutations/removeStockReasons.mutation';
import { GET_REMOVE_STOCK_REASONS } from 'src/graphql/queries/removeStockReasons.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import './RemoveStockReasonsPageStyles.css';

const RemoveStockReasonsPage = () => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openRemoveStockReasonModal, setOpenRemoveStockModal] = useState(false);
    const [id, setId] = useState<number | null>();
    const [description, setDescription] = useState('');
    const { user } = useAuthContext();

    const { data, loading, refetch } = useQuery(GET_REMOVE_STOCK_REASONS, {
        fetchPolicy: 'cache-and-network',
        onError: () => showErrorToast('There was an error retrieving remove stock reasons'),
    });

    const [deleteRemoveStockReason] = useMutation(DELETE_REMOVE_STOCK_REASON_MUTATION, {
        onCompleted: () => {
            showSuccessToast('Remove Stock Reason Deleted Successfully');
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the Reason');
        },
    });

    const tableData = useMemo(() => {
        if (data?.removeStockReasons?.length) {
            return data.removeStockReasons.map((reason) => {
                return {
                    ...reason,
                    dateCreated: reason.createdAt,
                    status: <StatusChip status={reason?.status} type="cancellationReasons" />,
                };
            });
        }
        return [];
    }, [data]);

    const handleOpenDeleteModal = (id) => {
        setId(id);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    // Function used to delete the specified remove stock reason
    const handleDeleteRecord = () => {
        // GraphQL mutation
        deleteRemoveStockReason({ variables: { id: id } });
        handleCloseDeleteModal();
    };

    const handleCloseRemoveStockReasonModal = () => {
        setOpenRemoveStockModal(false);
    };
    const handleAddRemoveStockReason = () => {
        // Open add reason modal
        setOpenRemoveStockModal(true);
    };

    const [createRemoveStockReasonMutation, { loading: creatingNewCancellationReason }] =
        useMutation(CREATE_REMOVE_STOCK_REASON_MUTATION, {
            onCompleted: () => {
                handleCloseRemoveStockReasonModal();
                showSuccessToast('Remove Stock Reason Created Successfully');
                refetch();
            },
            onError: () => {
                showErrorToast('Error Creating Remove Stock Reason');
            },

            variables: {
                input: {
                    status: ConfigurationStatus.ACTIVE, // DEFAULT
                    description,
                    createdBy: user.id,
                    lastModifiedBy: user.id,
                },
            },
        });

    return (
        <PageContent
            heading="Remove Stock Reasons"
            subHeading="Add, view and delete remove stock reasons"
        >
            <MetaTags title="Remove Stock Reasons" description="Remove Stock Reasons Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Remove Stock Reason"
                            data={tableData}
                            headers={REMOVE_STOCK_REASONS_TABLE_HEADERS}
                            title="Remove Stock Reasons List"
                            addRecordCallBack={handleAddRemoveStockReason}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            enableExport
                            filterOptions={{}}
                        />
                    )}

                    <Dialog
                        open={openDeleteModal}
                        onClose={handleCloseDeleteModal}
                        aria-labelledby="delete-confirmation-reason-dialog-title"
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '400px',
                            },
                        }}
                    >
                        <DialogContent>
                            <ModalContent
                                handleClose={handleCloseDeleteModal}
                                handleAction={handleDeleteRecord}
                                header="Delete Remove Stock Reason"
                                message="Are you sure you want to delete this Remove Stock Reason?"
                                buttonText="Confirm"
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog
                        open={openRemoveStockReasonModal}
                        onClose={handleCloseRemoveStockReasonModal}
                        aria-labelledby="delete-confirmation-reason-dialog-title"
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '400px',
                            },
                        }}
                    >
                        <DialogContent>
                            <ModalContent
                                handleClose={handleCloseRemoveStockReasonModal}
                                handleAction={createRemoveStockReasonMutation}
                                header="Add Remove Stock Reason"
                                isEdit={true}
                                buttonText="Confirm"
                            >
                                <Formik>
                                    <Grid item xs={12} md={12}>
                                        <CustomInput
                                            name="Remove Stock Reason"
                                            label="Remove Stock Reason"
                                            maxLength={50}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </Grid>
                                </Formik>
                            </ModalContent>
                        </DialogContent>
                    </Dialog>
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default RemoveStockReasonsPage;
